import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { getAllUsersJobsAsync } from "../../../services/UserJobsServices";
import { getUserByIdAsync } from "../../../services/DashboardUsers";
import { asyncStatus } from "../../../utils/asyncStatus";
import { themeOrange, themeBlue } from "../../../utils/colorTheme";

const statusColors = {
  Completed: { bg: "#E8F5E9", color: "#2E7D32" },
  "In Progress": { bg: "#FFF3E0", color: "#E65100" },
  Assigned: { bg: "#E3F2FD", color: "#1565C0" },
  Pending: { bg: "#FFF8E1", color: "#F9A825" },
  Cancelled: { bg: "#FFEBEE", color: "#C62828" },
  "En Route": { bg: "#E8EAF6", color: "#283593" },
  "Aprroval-Waiting": { bg: "#F3E5F5", color: "#6A1B9A" },
};

const VendorJobsTab = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { get_all_user_jobs_status, get_all_user_jobs_data } = useSelector(
    (state) => state.userJobsSlice
  );
  const { get_user_by_id_data } = useSelector(
    (state) => state.dashboard_users
  );

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  useEffect(() => {
    dispatch(getAllUsersJobsAsync());
    if (!get_user_by_id_data) {
      dispatch(getUserByIdAsync(id));
    }
  }, []);

  const vendorJobs = useMemo(() => {
    const orders = Array.isArray(get_all_user_jobs_data)
      ? get_all_user_jobs_data
      : get_all_user_jobs_data?.orders ?? [];
    return orders.filter(
      (job) => String(job.vendor_id) === String(id)
    );
  }, [get_all_user_jobs_data, id]);

  const stats = useMemo(() => {
    const completed = vendorJobs.filter((j) => j.status === "Completed").length;
    const inProgress = vendorJobs.filter(
      (j) => j.status === "In Progress" || j.status === "En Route"
    ).length;
    const pending = vendorJobs.filter(
      (j) => j.status === "Assigned" || j.status === "Pending"
    ).length;
    const cancelled = vendorJobs.filter(
      (j) => j.status === "Cancelled"
    ).length;
    const totalEarned = vendorJobs
      .filter((j) => j.status === "Completed")
      .reduce((sum, j) => sum + (+j.total_amount || 0), 0);
    const successRate =
      vendorJobs.length > 0
        ? Math.round((completed / vendorJobs.length) * 100)
        : 0;

    return {
      total: vendorJobs.length,
      completed,
      inProgress,
      pending,
      cancelled,
      totalEarned,
      successRate,
    };
  }, [vendorJobs]);

  if (get_all_user_jobs_status === asyncStatus.LOADING) {
    return (
      <Stack alignItems="center" justifyContent="center" height="60vh">
        <CircularProgress size={30} sx={{ color: themeOrange }} />
      </Stack>
    );
  }

  const statCards = [
    { label: "Total Jobs", value: stats.total, bg: themeBlue, color: "#fff" },
    { label: "Completed", value: stats.completed, bg: "#E8F5E9", color: "#2E7D32" },
    { label: "In Progress", value: stats.inProgress, bg: "#FFF3E0", color: "#E65100" },
    { label: "Pending", value: stats.pending, bg: "#FFF8E1", color: "#F9A825" },
    { label: "Cancelled", value: stats.cancelled, bg: "#FFEBEE", color: "#C62828" },
    { label: "Success Rate", value: `${stats.successRate}%`, bg: "#F3E5F5", color: "#6A1B9A" },
  ];

  return (
    <Stack bgcolor="#FAFAFA">
      <Divider />
      <Container maxWidth="lg">
        <Stack gap={3} mt={2} mb={4}>
          {/* ── Stats Cards ── */}
          <Stack>
            <Typography className="mainHeading" fontWeight={700}>
              Job Statistics
            </Typography>
            <Grid container spacing={2} mt={1}>
              {statCards.map((stat, i) => (
                <Grid item xs={6} sm={4} md={2} key={i}>
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      bgcolor: "white",
                      borderRadius: "10px",
                      boxShadow: "0px 4px 10px 0px #00000026",
                      p: 2,
                      minHeight: "100px",
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        bgcolor: stat.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 700,
                          color: stat.color,
                        }}
                      >
                        {stat.value}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#666",
                        textAlign: "center",
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>

            {/* Earnings card */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                bgcolor: "white",
                borderRadius: "10px",
                boxShadow: "0px 4px 10px 0px #00000026",
                p: 2.5,
                mt: 2,
              }}
            >
              <Typography sx={{ fontSize: "15px", fontWeight: 600, color: themeBlue }}>
                Total Earnings (Completed Jobs)
              </Typography>
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: 700,
                  color: "#2E7D32",
                }}
              >
                ${stats.totalEarned.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Stack>
          </Stack>

          {/* ── Job History Table ── */}
          <Stack>
            <Typography className="mainHeading" fontWeight={700}>
              Job History
            </Typography>
            <Stack
              bgcolor="white"
              mt={1}
              sx={{
                boxShadow: "0px 4px 10px 0px #00000026",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              {vendorJobs.length === 0 ? (
                <Stack alignItems="center" justifyContent="center" p={5}>
                  <Typography sx={{ color: "#999", fontSize: "15px" }}>
                    No jobs found for this vendor.
                  </Typography>
                </Stack>
              ) : (
                <>
                  <TableContainer sx={{ maxHeight: 500 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {["#", "Service", "Customer", "Location", "Date", "Amount", "Status"].map(
                            (col) => (
                              <TableCell
                                key={col}
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "13px",
                                  color: themeBlue,
                                  bgcolor: "#F5F5F5",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {col}
                              </TableCell>
                            )
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {vendorJobs
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((job, index) => {
                            const statusStyle = statusColors[job.status] || {
                              bg: "#eee",
                              color: "#333",
                            };
                            const jobDate = job.created_at
                              ? new Date(job.created_at).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "-";
                            return (
                              <TableRow key={job.id} hover>
                                <TableCell sx={{ fontSize: "13px" }}>
                                  {page * rowsPerPage + index + 1}
                                </TableCell>
                                <TableCell sx={{ fontSize: "13px", fontWeight: 500 }}>
                                  {job.service?.name || "-"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "13px" }}>
                                  {job.user
                                    ? `${job.user.first_name || ""} ${job.user.last_name || ""}`.trim()
                                    : "-"}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontSize: "12px",
                                    maxWidth: 180,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {job.location || "-"}
                                </TableCell>
                                <TableCell sx={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                                  {jobDate}
                                </TableCell>
                                <TableCell sx={{ fontSize: "13px", fontWeight: 600 }}>
                                  ${(+job.total_amount || 0).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={job.status || "Unknown"}
                                    size="small"
                                    sx={{
                                      bgcolor: statusStyle.bg,
                                      color: statusStyle.color,
                                      fontWeight: 600,
                                      fontSize: "11px",
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={vendorJobs.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                  />
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </Stack>
  );
};

export default VendorJobsTab;
